import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bell, ChevronDown, X, Send, UserPlus } from "lucide-react";
import { Switch } from "@/components/ui/switch";

// Admin corner — who hears about a breakdown, and when.
// The list is RockLink PEOPLE, picked by name (a deactivated user drops off on
// their own). Addresses from before the list became people, that belong to no
// user, stay as "other addresses" — they're still mailed, and can still be
// added for someone outside RockLink (e.g. the chairman's personal inbox).
// Three alerts, all silent with nobody on the list:
//   · the moment a STOPPED machine is reported (the reporter can adjust who)
//   · one email when a stopped machine passes 4 clocked hours down — ONCE
//   · an optional 08:00 digest of everything still open
// The provider is never named in the UI: it is "the email service".

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MaintenanceAlertsPanel() {
  const [open, setOpen] = useState(false);
  const [st, setSt] = useState(null);
  const [people, setPeople] = useState([]);
  const [query, setQuery] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const pickerRef = useRef(null);

  useEffect(() => {
    if (!open || st) return;
    axios.get(`${API}/maintenance/alerts/settings`)
      .then((r) => setSt(r.data))
      .catch(() => toast.error("Could not load the alert settings."));
    axios.get(`${API}/users/mentionables`)
      .then((r) => setPeople(r.data || []))
      .catch(() => {});
  }, [open, st]);

  useEffect(() => {
    if (!pickerOpen) return;
    const onDown = (e) => { if (pickerRef.current && !pickerRef.current.contains(e.target)) setPickerOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [pickerOpen]);

  const save = useCallback(async (next) => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/maintenance/alerts/settings`, {
        recipient_user_ids: next.recipient_user_ids || [],
        recipients: next.recipients || [],
        alert_on_report_enabled: !!next.alert_on_report_enabled,
        alert_down_enabled: !!next.alert_down_enabled,
        daily_digest_enabled: !!next.daily_digest_enabled,
      });
      setSt(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save that.");
    } finally { setBusy(false); }
  }, []);

  const ids = st?.recipient_user_ids || [];
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return people
      .filter((p) => !ids.includes(p.id))
      .filter((p) => !q || `${p.name} ${p.email} ${p.role}`.toLowerCase().includes(q))
      .slice(0, 8);
  }, [people, ids, query]);

  const addPerson = (p) => {
    setQuery(""); setPickerOpen(false);
    save({ ...st, recipient_user_ids: [...ids, p.id] });
  };

  const addAddress = () => {
    const e = draft.trim().toLowerCase();
    if (!e || !e.includes("@")) { toast.error("That doesn't look like an email address."); return; }
    setDraft("");
    const user = people.find((p) => (p.email || "").toLowerCase() === e);
    if (user) { if (!ids.includes(user.id)) save({ ...st, recipient_user_ids: [...ids, user.id] }); return; }
    if ((st.recipients || []).includes(e)) return;
    save({ ...st, recipients: [...(st.recipients || []), e] });
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/maintenance/alerts/test`);
      toast.success(`Test sent to ${r.data?.sent_to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the test.");
    } finally { setBusy(false); }
  };

  const count = st ? (st.recipient_users || []).length + (st.recipients || []).length : null;

  const chip = (key, label, sub, onRemove, testid) => (
    <span key={key} className="rl-pill inline-flex items-center gap-1.5 h-8 pl-3 pr-2 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[12px]">
      <span className="font-semibold">{label}</span>
      {sub && <span className="text-[11px] text-[hsl(var(--muted-foreground))]">{sub}</span>}
      <button
        onClick={onRemove}
        aria-label={`Remove ${label}`}
        title="Take off the list"
        data-testid={testid}
        className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </span>
  );

  const row = (title, sub, checked, onChange, label, testid) => (
    <div className="flex items-center justify-between gap-3 py-2 border-t border-[hsl(var(--border))]/60">
      <div>
        <div className="text-[12.5px] font-semibold">{title}</div>
        <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{sub}</div>
      </div>
      <Switch checked={!!checked} onCheckedChange={onChange} aria-label={label} data-testid={testid} />
    </div>
  );

  return (
    <div className="rl-glass-edge rounded-[14px] bg-[hsl(var(--card))] mt-4 overflow-hidden" data-testid="mt-alerts-panel">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-[18px] py-3.5 text-left"
        data-testid="mt-alerts-toggle"
      >
        <Bell className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
        <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-[hsl(var(--foreground))]/85">Alerts &amp; recipients</span>
        <span className="text-[11px] text-[hsl(var(--muted-foreground))]">
          {count === null ? "admin only" : count === 0 ? "nobody is being emailed yet" : `${count} recipient${count === 1 ? "" : "s"}`}
        </span>
        <ChevronDown className={`w-4 h-4 ml-auto text-[hsl(var(--muted-foreground))] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="border-t border-[hsl(var(--border))] px-[18px] py-4">
          {!st && <p className="text-[12px] text-[hsl(var(--muted-foreground))]">Loading…</p>}
          {st && (
            <>
              <p className="text-[11px] text-[hsl(var(--muted-foreground))] max-w-[560px] leading-relaxed mb-3">
                Alerts go to these people. With nobody on the list nothing is ever sent —
                which is a valid state, not an error.
              </p>

              <div className="flex flex-wrap gap-2 mb-3" data-testid="mt-alert-people">
                {(st.recipient_users || []).map((u) => chip(
                  u.id, u.name, u.role,
                  () => save({ ...st, recipient_user_ids: ids.filter((x) => x !== u.id) }),
                  `mt-alert-remove-${u.id}`))}
                {(st.recipients || []).map((e) => chip(
                  e, e, "other address",
                  () => save({ ...st, recipients: st.recipients.filter((x) => x !== e) }),
                  `mt-alert-remove-${e}`))}
                {count === 0 && (
                  <span className="text-[12px] text-[hsl(var(--muted-foreground))] italic">Nobody yet</span>
                )}
              </div>

              <div className="flex items-center gap-2 flex-wrap mb-4">
                <div className="relative" ref={pickerRef}>
                  <div className="flex items-center gap-1.5 h-8 w-[240px] rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-2.5 focus-within:border-[hsl(var(--primary))]/60">
                    <UserPlus className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))] shrink-0" />
                    <input
                      value={query}
                      onChange={(e) => { setQuery(e.target.value); setPickerOpen(true); }}
                      onFocus={() => setPickerOpen(true)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && matches[0]) { e.preventDefault(); addPerson(matches[0]); }
                        if (e.key === "Escape") setPickerOpen(false);
                      }}
                      placeholder="Add a person…"
                      data-testid="mt-alert-person-input"
                      className="bg-transparent outline-none text-[12.5px] w-full"
                    />
                  </div>
                  {pickerOpen && (
                    <div className="absolute z-20 mt-1 w-[300px] rounded-[10px] border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-lg py-1" data-testid="mt-alert-person-list">
                      {matches.length === 0 && (
                        <div className="px-3 py-2 text-[12px] text-[hsl(var(--muted-foreground))]">
                          {people.length ? "Nobody else matches" : "Loading people…"}
                        </div>
                      )}
                      {matches.map((p) => (
                        <button
                          key={p.id}
                          onMouseDown={(e) => { e.preventDefault(); addPerson(p); }}
                          className="w-full text-left px-3 py-1.5 hover:bg-[hsl(var(--muted))]/60 flex items-baseline gap-2"
                          data-testid={`mt-alert-person-${p.id}`}
                        >
                          <span className="text-[12.5px] font-semibold truncate">{p.name || p.email}</span>
                          <span className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">{p.role}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  onClick={sendTest}
                  disabled={busy}
                  title="Sends one sample to your own address"
                  data-testid="mt-alert-test"
                  className="rl-pill inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[12px] font-semibold disabled:opacity-50"
                >
                  <Send className="w-3.5 h-3.5" /> Send a test
                </button>
                {!st.email_configured && (
                  <span className="text-[11px] rl-tx-amber" title="Nothing will be sent until the email service is configured">
                    email service not set up here
                  </span>
                )}
              </div>

              <details className="mb-3 group">
                <summary className="text-[11px] text-[hsl(var(--muted-foreground))] cursor-pointer select-none hover:text-[hsl(var(--foreground))]">
                  Someone without a RockLink login? Add their address
                </summary>
                <div className="flex items-center gap-2 mt-2">
                  <input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addAddress(); } }}
                    placeholder="name@example.com"
                    data-testid="mt-alert-email-input"
                    className="h-8 w-[240px] rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-[12.5px] outline-none focus:border-[hsl(var(--primary))]/60"
                  />
                  <button
                    onClick={addAddress}
                    disabled={busy}
                    data-testid="mt-alert-email-add"
                    className="rl-pill h-8 px-3.5 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[12px] font-semibold disabled:opacity-50"
                  >
                    Add
                  </button>
                </div>
              </details>

              {row("Email the moment a breakdown is reported",
                "Stopped machines only — a machine still producing never mails. Whoever reports it sees this list ticked and can change it for that one breakdown.",
                st.alert_on_report_enabled, (v) => save({ ...st, alert_on_report_enabled: v }),
                "Email when a breakdown is reported", "mt-alert-report-switch")}
              {row(`Machine down over ${st.alert_after_hours ?? 4} hours`,
                `One email per breakdown, the first time it passes ${st.alert_after_hours ?? 4} hours on the clock. The 10-minute grace isn't counted.`,
                st.alert_down_enabled, (v) => save({ ...st, alert_down_enabled: v }),
                "Machine down alert", "mt-alert-down-switch")}
              {row("Daily 08:00 digest",
                "Every open breakdown with its stage and how long it's been down. Skipped when nothing is open.",
                st.daily_digest_enabled, (v) => save({ ...st, daily_digest_enabled: v }),
                "Daily digest", "mt-alert-digest-switch")}
            </>
          )}
        </div>
      )}
    </div>
  );
}
